import { LanguageSelectors, renderStringToReact, useTranslation } from '@web_opxp/features';
import { orderBy } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { SKELETON_CLASSNAME, SKELETON_DUMMYTEXT } from '../../base/constants';
import { formatDate } from '../../base/dateUtils';
import { EDomain } from '../../base/types';
import HintPopup from '../../components/HintPopup/HintPopup';
import ProgressGraph from '../../components/progress/ProgressGraph';
import { CyclesSelectors } from '../../cycles';
import { IState } from '../../store/state';
import * as Selectors from '../selectors';
import { IDomainProgress } from '../types';
import { findNewerTime } from '../utils';
import './EvaluationHistogram.scss';

interface IOwnProps {
  domain: EDomain | null;
  userId?: number;
}

export interface IEvaluationHistogramProps extends IOwnProps {
  domainResults: IDomainProgress[];

  loaded: boolean;
  lastAssessment?: string;
  sessionsLeft?: number;
}

export const EvaluationHistogramFC: React.FC<IEvaluationHistogramProps> = ({
  domainResults,
  loaded,
  domain,
  sessionsLeft,
  lastAssessment,
  userId,
}) => {
  const { t, language } = useTranslation();

  const skeletonClass = !loaded || !language ? SKELETON_CLASSNAME : '';
  const domainName = t(`common:Domain.Title.${domain}`);

  let title: string;

  if (!loaded || !language) {
    title = SKELETON_DUMMYTEXT;
  } else if (userId && userId !== 0) {
    title = t('Evaluation.Histogram.UserTitle', { domain: domainName });
  } else {
    title = t('Evaluation.Histogram.Title', { domain: domainName });
  }

  const noDataLabel = <span className={skeletonClass}> No Data </span>;
  const sortedDomainResults = orderBy(domainResults, ['time']);

  const domainString = domain ? EDomain[domain] : EDomain[EDomain.Assessment];

  const buttonLabel = t(`Evaluation.HintDialogOKLabel.${domainString}`);
  const hintPopupTitle = t(`Evaluation.HintDialogTitle.${domainString}`);
  const hintPopupContent = t(`Evaluation.HintContentHtml.${domainString}`);

  const contentReactElement = renderStringToReact(hintPopupContent);

  return (
    <div className="evaluation-histogram">
      <h2 className={skeletonClass}>
        {title}
        {!userId ? (
          <HintPopup title={hintPopupTitle} iconSize={20} okButtonLabel={buttonLabel}>
            {contentReactElement}
          </HintPopup>
        ) : null}
      </h2>
      <ProgressGraph
        locale={language}
        noDataLabel={noDataLabel}
        progress={sortedDomainResults}
        useNormalizedScore={true}
      />
      {!userId ? (
        <p className="evaluation-histogram-description nn-small">
          {t('Evaluation.Histogram.Description', { sessionsLeft, lastAssessment })}
        </p>
      ) : null}
    </div>
  );
};

const mapStateToProps = (state: IState, { domain }: IOwnProps) => {
  const language = LanguageSelectors.getLanguage(state);
  const loaded = !!domain && !!language;
  let domainResults: IDomainProgress[] = [],
    lastAssessment = '',
    sessionsLeft: number | undefined;
  if (loaded) {
    domainResults = Selectors.getDomainResults(state, domain);
    sessionsLeft = loaded ? CyclesSelectors.getSessionsLeftCount(state) : undefined;
    lastAssessment = domainResults.length ? formatDate(findNewerTime(domainResults), language) : '';
  }
  return {
    loaded,
    domainResults,
    lastAssessment,
    sessionsLeft,
  };
};

const EvaluationHistogram = connect(mapStateToProps)(EvaluationHistogramFC);
export default EvaluationHistogram;
